import { useTranslations } from '@fshr/context/i18n-context';
import { generateUniqueId } from '@fshr/shared';
import { cn } from '@fshr/shared-react';
import { forwardRef, ReactNode } from 'react';

type Value = boolean | string | number | null;

interface CheckboxProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'onBlur'> {
	options?: {
		checked: Value;
		unchecked: Value;
	};
	onChange?: (value: Value, evt: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (value: Value, evt: React.FocusEvent<HTMLInputElement>) => void;
	label?: ReactNode;
	labelClassName?: string;
	labelTextClassName?: string;
	wrapperClassName?: string;
	inputClassName?: string;
	errorMessage?: ReactNode;
}

const Checkbox = forwardRef(
	(
		{
			className = '',
			checked,
			options = { checked: true, unchecked: false },
			onChange,
			onBlur,
			disabled,
			label,
			labelClassName = '',
			wrapperClassName = '',
			inputClassName = '',
			errorMessage,
			labelTextClassName,
			...props
		}: CheckboxProps,
		ref: React.Ref<HTMLInputElement>
	) => {
		const handleBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
			onBlur?.(evt.target.checked ? options.checked : options.unchecked, evt);
		};
		const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(evt.target.checked ? options.checked : options.unchecked, evt);
		};
		const id = props.id || generateUniqueId('checkbox');
		const t = useTranslations();
		return (
			<div className="flex flex-col gap-100">
				<div className={cn('justify-start items-start gap-[12px] inline-flex', wrapperClassName)}>
					<div className={`relative inline-block h-200 ${className}`}>
						<input
							type="checkbox"
							checked={checked}
							disabled={disabled}
							ref={ref}
							{...props}
							className={cn(
								'peer h-200 w-200 appearance-none border border-gray-300 cursor-pointer bg-white',
								{
									'disabled:border-button-primary-disabled cursor-not-allowed': disabled,
									'border-red-700': errorMessage,
								},
								inputClassName
							)}
							onChange={handleChange}
							onBlur={handleBlur}
							id={id}
							aria-invalid={errorMessage ? true : undefined}
							aria-describedby={errorMessage ? `${id}-error` : props['aria-describedby']}
						/>
						{!disabled ? (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="pointer-events-none absolute left-0 top-0 h-200 w-200 stroke-white opacity-0 peer-checked:opacity-100"
							>
								<g id="icons/navigation&#38;actions/checkbox/on">
									<path
										id="Combined Shape"
										fillRule="evenodd"
										clipRule="evenodd"
										d="M20 0.5H0V20.5H20V0.5ZM5 12.5V10.5L9 14.5L16 5.5V7.5L9 16.5L5 12.5Z"
										fill="#030303"
										strokeWidth={0.5}
									/>
								</g>
							</svg>
						) : (
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
								className="pointer-events-none absolute left-0 top-0 h-200 w-200 stroke-white opacity-0 peer-checked:opacity-100"
							>
								<g id="icons/checkbox/on">
									<path
										id="Combined Shape"
										fillRule="evenodd"
										clipRule="evenodd"
										d="M20 0.5H0V20.5H20V0.5ZM5 12.5V10.5L9 14.5L16 5.5V7.5L9 16.5L5 12.5Z"
										fill="#8C8C8C"
										strokeWidth={0.5}
									/>
								</g>
							</svg>
						)}
					</div>
					{typeof label === 'string' ? (
						<label htmlFor={id} className={labelClassName || 'text-label'}>
							<span className={cn(labelTextClassName)}>{t(label)}</span>
						</label>
					) : (
						label
					)}
				</div>
				{errorMessage && (
					<span id={`${id}-error`} className="text-left text-caption text-red-700" role="alert">
						{errorMessage}
					</span>
				)}
			</div>
		);
	}
);

export default Checkbox;
